import { useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { API } from "../constants/site";
import Logo from "./Logo";

function VisaMark() {
  return (
    <span className="flex h-8 w-12 items-center justify-center rounded-md border border-oat-100/20 bg-oat-100/5 text-[11px] font-bold italic tracking-wider text-oat-100">
      VISA
    </span>
  );
}

function MastercardMark() {
  return (
    <span className="flex h-8 w-12 items-center justify-center gap-1 rounded-md border border-oat-100/20 bg-oat-100/5">
      <span className="h-3.5 w-3.5 rounded-full bg-[#EB001B]" />
      <span className="-ml-2 h-3.5 w-3.5 rounded-full bg-[#F79E1B] opacity-90" />
    </span>
  );
}

function PayPalMark() {
  return (
    <span className="flex h-8 w-12 items-center justify-center rounded-md border border-oat-100/20 bg-oat-100/5 text-[11px] font-bold italic tracking-wide text-oat-100">
      <span className="text-[#7FB3E8]">Pay</span>
      <span className="-ml-0.5 text-[#2E9CDB]">Pal</span>
    </span>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      toast.success(data.message || "You're on the list");
      setEmail("");
    } catch (err) {
      toast.error(err.message || "Could not subscribe right now");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="relative bg-charcoal-900 text-oat-100" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 lg:px-10 lg:pt-28">
        {/* Newsletter */}
        <div className="flex flex-wrap items-end justify-between gap-10 border-b border-oat-100/10 pb-14">
          <div className="max-w-md">
            <h3 className="font-serif text-3xl leading-snug md:text-4xl">
              Join the <em className="italic text-sage-400">Kitchen Craft</em> list
            </h3>
            <p className="mt-3 text-base text-oat-100/60">
              Recipe notes, mist-mode tricks and early access to new colours.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md gap-3" data-testid="newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@kitchen.com"
              data-testid="newsletter-email-input"
              className="h-13 w-full rounded-full border border-oat-100/20 bg-oat-100/5 px-6 py-3.5 text-base sm:text-sm text-oat-100 placeholder:text-oat-100/40 focus:border-sage-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="newsletter-submit-btn"
              className="shrink-0 rounded-full bg-sage-500 px-7 py-3.5 text-sm font-medium uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-sage-600 disabled:opacity-60"
            >
              {sending ? "Joining…" : "Subscribe"}
            </button>
          </form>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 gap-12 py-14 md:grid-cols-12">
          <div className="md:col-span-3">
            <Logo light />
            <p className="mt-6 max-w-xs font-serif text-2xl italic leading-snug text-oat-100/80">
              “Spray fine mist, pour with precision.”
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-oat-100/50">
              Shop
            </p>
            <ul className="space-y-3 text-sm text-oat-100/70">
              {["The Sprayer", "Features", "Gallery", "Unboxing"].map((l) => (
                <li key={l}>
                  <a href={l === "The Sprayer" ? "#top" : `#${l.split(" ")[0].toLowerCase() === "unboxing" ? "video" : l.split(" ")[0].toLowerCase()}`} className="transition-colors hover:text-sage-400">
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-oat-100/50">
              Support
            </p>
            <ul className="space-y-3 text-sm text-oat-100/70">
              <li><a href="#faq" className="transition-colors hover:text-sage-400">FAQ</a></li>
              <li><a href="/shipping" data-testid="footer-shipping-link" className="transition-colors hover:text-sage-400">Shipping &amp; Delivery</a></li>
              <li><a href="/returns" data-testid="footer-returns-link" className="transition-colors hover:text-sage-400">Return &amp; Refund</a></li>
              <li><a href="mailto:hello@omnimist.in" data-testid="footer-email-link" className="transition-colors hover:text-sage-400">hello@omnimist.in</a></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-oat-100/50">
              Customer Care
            </p>
            <ul className="space-y-3 text-sm text-oat-100/70">
              <li>
                <a href="/track" data-testid="footer-track-link" className="transition-colors hover:text-sage-400">
                  Track your order
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/919032835563?text=Hi%20OmniMist!%20I%20have%20a%20question."
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="transition-colors hover:text-sage-400"
                >
                  WhatsApp us · 90328 35563
                </a>
              </li>
              <li>
                <a href="mailto:hello@omnimist.in" className="transition-colors hover:text-sage-400" data-testid="footer-email-link">
                  hello@omnimist.in
                </a>
              </li>
              <li>Replies within 1 business day</li>
              <li>Mon–Sat · 10am–7pm IST</li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.24em] text-oat-100/50">
              Secure Checkout
            </p>
            <div className="flex flex-wrap items-center gap-2" data-testid="payment-badges">
              <span className="flex h-8 items-center gap-1.5 rounded-md border border-oat-100/20 bg-oat-100/5 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-oat-100/80">
                <Lock className="h-3 w-3 text-sage-400" /> SSL
              </span>
              <VisaMark />
              <MastercardMark />
              <PayPalMark />
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs text-oat-100/50">
              <ShieldCheck className="h-4 w-4 text-sage-400" />
              256-bit encrypted checkout · Cash on Delivery available
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-oat-100/10 pt-8 text-xs text-oat-100/40">
          <p>© {new Date().getFullYear()} OmniMist Kitchen Craft. All rights reserved.</p>
          <p className="flex flex-wrap items-center gap-4" data-testid="footer-legal-links">
            <a href="/about" className="transition-colors hover:text-sage-400">About Us</a>
            <a href="/privacy" className="transition-colors hover:text-sage-400">Privacy Policy</a>
            <a href="/terms" className="transition-colors hover:text-sage-400">Terms &amp; Conditions</a>
            <a href="/shipping" className="transition-colors hover:text-sage-400">Shipping Policy</a>
            <a href="/returns" className="transition-colors hover:text-sage-400">Refund Policy</a>
          </p>
          <p>2-in-1 Oil Sprayer &amp; Dispenser · 470ml Food-Grade Borosilicate Glass</p>
        </div>
      </div>
    </footer>
  );
}
